if (!customElements.get('gift-card-selector')) {
  customElements.define(
    'gift-card-selector',
    class GiftCardSelector extends HTMLElement {
      constructor() {
        super();
        
        this.currentProduct = null;
        this.currentVariant = null;
        this.pendingFormData = null;
        this.recipientForm = null;
        this.isSubmitting = false;
        
        this.setupEventListeners();
      }

      setupEventListeners() {
        // Overlay click to close
        this.addEventListener('click', (event) => {
          if (event.target.matches('.gift-card-selector__overlay')) {
            this.close();
          }
        });

        // Close button click
        this.addEventListener('click', (event) => {
          if (event.target.closest('.gift-card-selector__close-button')) {
            this.close();
          }
        });

        // Add to cart button click
        this.addEventListener('click', (event) => {
          if (event.target.closest('.gift-card-selector__add-to-cart-button')) {
            this.handleAddToCart();
          }
        });

        // Escape key to close
        document.addEventListener('keyup', (evt) => {
          if (evt.code === 'Escape' && this.classList.contains('active')) {
            this.close();
          }
        });
      }

      showGiftCardForm(product, variant, formData) {
        this.currentProduct = product;
        this.currentVariant = variant;
        this.pendingFormData = formData;
        
        this.populateHeader();
        this.updateAddToCartButton();
        this.open();
        
        // Initialize recipient form after a small delay to ensure DOM is ready
        setTimeout(() => {
          this.initializeRecipientForm();
        }, 100);
      }

      populateHeader() {
        const headerTitle = this.querySelector('.gift-card-selector__header-title');
        if (headerTitle && this.currentProduct && this.currentVariant) {
          const productTitle = this.currentProduct.title || 'Gift Card';
          const variantPrice = this.formatPrice(this.currentVariant.price);
          headerTitle.textContent = `${productTitle} • ${variantPrice}`;
        }
      }

      formatPrice(price) {
        // Price comes in cents, convert to dollars
        if (typeof price === 'string') {
          price = parseInt(price);
        }
        if (isNaN(price)) {
          return '$0.00';
        }
        
        const priceInDollars = price / 100;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(priceInDollars);
      }

      initializeRecipientForm() {
        this.recipientForm = this.querySelector('recipient-form');
        
        if (this.recipientForm) {
          // Update the data attributes with the current variant ID
          if (this.currentVariant) {
            this.recipientForm.setAttribute('data-product-variant-id', this.currentVariant.id);
          }
          
          // Reset the form
          this.resetRecipientForm();

          const inputFields = this.recipientForm.querySelectorAll('input, textarea');
          inputFields.forEach(field => {
            if (field.type !== 'hidden') {
              field.disabled = false;
            }
          });
          
          // Force show the recipient fields container
          const recipientFields = this.recipientForm.querySelector('.recipient-fields');
          if (recipientFields) {
            recipientFields.style.display = 'block';
            recipientFields.style.visibility = 'visible';
          }
          
          // Trigger the recipient form's initialization if it has a method
          if (this.recipientForm.enableInputFields) {
            this.recipientForm.enableInputFields();
          }
          
          // Manually call onChange if the method exists
          if (this.recipientForm.onChange) {
            this.recipientForm.onChange();
          }

          // Update the timezone offset field
          const offsetInput = this.recipientForm.querySelector('[name*="__shopify_offset"]');
          if (offsetInput) {
            offsetInput.value = new Date().getTimezoneOffset().toString();
            offsetInput.disabled = false;
          }
        }
      }

      resetRecipientForm() {
        if (this.recipientForm) {
          // Clear all input fields
          const inputs = this.recipientForm.querySelectorAll('input, textarea');
          inputs.forEach(input => {
            if (input.type !== 'checkbox' && input.type !== 'hidden') {
              input.value = '';
            }
          });
          
          // Clear any error messages
          const errorMessages = this.recipientForm.querySelectorAll('.form__message:not(.hidden)');
          errorMessages.forEach(msg => msg.classList.add('hidden'));
          
          // Remove error states from fields
          const errorFields = this.recipientForm.querySelectorAll('.field--error');
          errorFields.forEach(field => field.classList.remove('field--error'));
          
          // Reset aria attributes
          const inputsWithAria = this.recipientForm.querySelectorAll('[aria-invalid="true"]');
          inputsWithAria.forEach(input => {
            input.setAttribute('aria-invalid', 'false');
            input.removeAttribute('aria-describedby');
          });
        }
      }

      updateAddToCartButton() {
        const button = this.querySelector('.gift-card-selector__add-to-cart-button');
        if (button) {
          button.disabled = false;
          const span = button.querySelector('span');
          if (span) {
            span.textContent = 'Add to Cart';
          }
        }
      }

      async handleAddToCart() {
        if (this.isSubmitting) return;
        
        const button = this.querySelector('.gift-card-selector__add-to-cart-button');
        const buttonText = button?.querySelector('span');
        const spinner = button?.querySelector('.loading__spinner');
        
        // Validate the recipient form first
        if (!this.validateRecipientForm()) {
          return;
        }
        
        this.isSubmitting = true;
        
        // Update button state
        if (button) {
          button.disabled = true;
          button.classList.add('loading');
        }
        if (buttonText) {
          buttonText.textContent = 'Adding...';
        }
        if (spinner) {
          spinner.classList.remove('hidden');
        }
        
        try {
          // Collect all form data including recipient details
          const formData = this.collectFormData();
          
          // Submit to cart
          await this.submitToCart(formData);
          
          // Success - close the modal
          this.close();
          
        } catch (error) {
          console.error('Error adding gift card to cart:', error);
          this.showError('Failed to add gift card to cart. Please try again.');
        } finally {
          this.isSubmitting = false;
          
          // Reset button state
          if (button) {
            button.disabled = false;
            button.classList.remove('loading');
          }
          if (buttonText) {
            buttonText.textContent = 'Add to Cart';
          }
          if (spinner) {
            spinner.classList.add('hidden');
          }
        }
      }

      validateRecipientForm() {
        if (!this.recipientForm) return true;
        
        let isValid = true;
        
        // In gift card selector, we're always sending as a gift (no checkbox to check)
        // Validate required fields directly
        const emailInput = this.recipientForm.querySelector('input[type="email"]');
        const nameInput = this.recipientForm.querySelector('input[name*="Recipient name"]');
        
        if (emailInput && !emailInput.value.trim()) {
          this.showFieldError(emailInput, 'Email is required');
          isValid = false;
        } else if (emailInput && !this.isValidEmail(emailInput.value.trim())) {
          this.showFieldError(emailInput, 'Please enter a valid email address');
          isValid = false;
        }
        
        if (nameInput && !nameInput.value.trim()) {
          this.showFieldError(nameInput, 'Recipient name is required');
          isValid = false;
        }
        
        return isValid;
      }

      showFieldError(input, message) {
        const field = input.closest('.field');
        if (field) {
          field.classList.add('field--error');
          
          // Find the error message element for this field
          const errorMessageId = input.getAttribute('aria-describedby');
          if (errorMessageId) {
            const errorElement = document.getElementById(errorMessageId);
            if (errorElement) {
              errorElement.classList.remove('hidden');
              const errorText = errorElement.querySelector('.error-message');
              if (errorText) {
                errorText.textContent = message + '.';
              }
            }
          }
          
          input.setAttribute('aria-invalid', 'true');
        }
      }

      isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      }

      collectFormData() {
        const formData = new FormData();

        if (this.currentVariant) {
          formData.append('id', this.currentVariant.id.toString());
        }

        formData.append('quantity', '1');

        // Add recipient form data in the exact format Shopify expects
        if (this.recipientForm) {
          const emailInput = this.recipientForm.querySelector('input[type="email"]');
          const nameInput = this.recipientForm.querySelector('input[name*="Recipient name"]');
          const messageInput = this.recipientForm.querySelector('textarea[name*="Message"]');
          const sendOnInput = this.recipientForm.querySelector('input[name*="Send on"]');

          if (emailInput && emailInput.value.trim() && nameInput && nameInput.value.trim()) {
            formData.append('properties[__shopify_send_gift_card_to_recipient]', 'if_present');

            formData.append('properties[Recipient email]', emailInput.value.trim());
            formData.append('properties[Recipient name]', nameInput.value.trim());

            if (messageInput && messageInput.value.trim()) {
              formData.append('properties[Message]', messageInput.value.trim());
            }

            if (sendOnInput && sendOnInput.value.trim()) {
              formData.append('properties[Send on]', sendOnInput.value.trim());
            }

            formData.append('properties[__shopify_offset]', new Date().getTimezoneOffset().toString());
          }
        }
        
        return formData;
      }

      async submitToCart(formData) {
        // Try without sections first to avoid redirect issues
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config)

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Cart add response not ok:', response.status, errorText);
          throw new Error(`Server error: ${response.status}`);
        }

        // Set flag to open cart after page refresh
        localStorage.setItem('openCartAfterRefresh', 'true');

        location.reload();
      }

      showError(message) {
        // You can implement a more sophisticated error display here
        // For now, just alert
        alert(message);
      }

      open() {
        this.classList.add('active');
        document.body.classList.add('overflow-hidden');

        requestAnimationFrame(() => {
          const firstInput = this.querySelector('input:not([type="hidden"]):not([type="checkbox"]), textarea');
          if (firstInput) {
            firstInput.focus();
          }
        });
      }

      close() {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');

        // Clear form data
        this.currentProduct = null;
        this.currentVariant = null;
        this.pendingFormData = null;
        this.resetRecipientForm();
      }
    }
  );
}
